import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { setCacheNamespace } from '@/shared/data/cacheNamespace';
import {
  ApiError,
  isCookieAuthToken,
  SESSION_EXPIRED_EVENT,
  setAuthErrorHandler,
  setAuthRefreshHandler,
  setAuthTokenProvider,
} from '@/shared/data/client';
import { authApi } from './api';
import { clearPostAuthRedirectAttempt } from './postAuthRedirectGuard';
import {
  getClerkProvisioningFlag,
  setSignOutTransitionFlag,
  getManualSignOutFlag,
  clearSessionExpiredFlag,
  getE2eAccessToken,
  getStoredSession,
  setManualSignOutFlag,
  setClerkProvisioningFlag,
  setSessionExpiredFlag,
  setStoredSession,
  STORAGE_KEY,
} from './sessionStorage';
import type { StoredSession } from './sessionStorage';
import type { AuthError, AuthGuardState, AuthSession, SignInPayload, SignUpPayload } from './types';
import type { ReactElement, ReactNode } from 'react';

type UseAuthResult = {
  session: AuthSession | null;
  loading: boolean;
  error: AuthError | null;
  signIn: (input: SignInPayload) => Promise<void>;
  signUp: (input: SignUpPayload) => Promise<void>;
  signOut: (options?: { redirect?: boolean; skipClerkSignOut?: boolean }) => void;
  refreshSession: () => Promise<boolean>;
  guardState: () => AuthGuardState;
  setSessionDirect: (next: AuthSession | null) => void;
};

type RefreshOutcome = {
  session: AuthSession | null;
  didRefresh: boolean;
};

const AuthContext = createContext<UseAuthResult | null>(null);

const mergeSessionFallbacks = (
  refreshed: AuthSession,
  fallback: AuthSession | null,
): AuthSession => {
  if (!fallback) return refreshed;
  if (fallback.userId !== refreshed.userId) {
    return refreshed;
  }
  return {
    ...refreshed,
    orgId: refreshed.orgId ?? fallback.orgId,
    orgName: refreshed.orgName ?? fallback.orgName,
    lastOrganisationId: refreshed.lastOrganisationId ?? fallback.lastOrganisationId,
    lastProjectId: refreshed.lastProjectId ?? fallback.lastProjectId,
    onboardingCompleted: refreshed.onboardingCompleted ?? fallback.onboardingCompleted,
    emailVerified: refreshed.emailVerified ?? fallback.emailVerified,
    roles: refreshed.roles ?? fallback.roles,
    superuserEmailMatched: refreshed.superuserEmailMatched ?? fallback.superuserEmailMatched,
  };
};

const normalizeStoredSession = (stored: StoredSession): AuthSession => ({
  ...stored,
  orgId: stored.orgId ?? undefined,
  orgName: stored.orgName ?? undefined,
});

const wait = async (ms: number): Promise<void> => {
  await new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
};

const bootstrapTimeoutMs = 20_000;
const bootstrapRetryIntervalMs = 400;
const isMissingUserIdentityError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  const message = error.message.toLowerCase();
  return error.code === 'AUTH_FAILED' && message.includes('missing user identity');
};

const isUserNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  const message = error.message.toLowerCase();
  if (error.code === 'AUTH_FAILED' && message.includes('user not found')) {
    return true;
  }
  return error.status === 404 && error.code === 'NOT_FOUND' && message.includes('user not found');
};

const isIdentityMismatchError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  if (error.code !== 'AUTH_FAILED') return false;
  return error.message.toLowerCase().includes('identity mismatch');
};

export const shouldPreserveSessionOnSignedOutSignal = (input: {
  hasSignedInSignal: boolean;
  currentSession: AuthSession | null;
}): boolean => {
  if (input.hasSignedInSignal) return false;
  return input.currentSession !== null;
};

export const shouldPreserveSessionOnRefreshMiss = (input: {
  refreshedSession: AuthSession | null;
  currentSession: AuthSession | null;
}): boolean => {
  if (input.refreshedSession) return false;
  return input.currentSession !== null;
};

const useAuthState = (): UseAuthResult => {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();
  const { client } = useClerk();
  const [session, setSession] = useState<AuthSession | null>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const queryClient = useQueryClient();
  const logoutTimer = useRef<number | null>(null);
  const refreshInFlight = useRef<Promise<RefreshOutcome> | null>(null);
  const previousUserIdRef = useRef<string | null>(null);
  const nextRefreshAllowedAtMs = useRef(0);
  const refreshCooldownMs = 1500;
  const refreshRateLimitBackoffMs = 2000;
  const hasClerkClientSession = (() => {
    const clientValue = client as unknown;
    if (!clientValue || typeof clientValue !== 'object') {
      return false;
    }
    const sessionsRaw = (clientValue as { sessions?: unknown }).sessions;
    if (Array.isArray(sessionsRaw) && sessionsRaw.length > 0) {
      return true;
    }
    const lastActiveSessionIdRaw = (clientValue as { lastActiveSessionId?: unknown })
      .lastActiveSessionId;
    return typeof lastActiveSessionIdRaw === 'string' && lastActiveSessionIdRaw.length > 0;
  })();
  const hasSignedInSignal = isSignedIn ? true : hasClerkClientSession;

  const loadClerkSession = useCallback(async (): Promise<AuthSession | null> => {
    const allowProvisioning =
      getClerkProvisioningFlag() ||
      (typeof window !== 'undefined' && window.location.pathname.startsWith('/onboarding'));
    const resolveTokenFromClientSessions = async (): Promise<string | null> => {
      const clientValue = client as unknown;
      if (!clientValue || typeof clientValue !== 'object') {
        return null;
      }
      const sessionsRaw = (clientValue as { sessions?: unknown }).sessions;
      if (!Array.isArray(sessionsRaw) || sessionsRaw.length === 0) {
        return null;
      }
      const lastActiveSessionIdRaw = (clientValue as { lastActiveSessionId?: unknown })
        .lastActiveSessionId;
      const lastActiveSessionId =
        typeof lastActiveSessionIdRaw === 'string' ? lastActiveSessionIdRaw : null;
      const preferredSession = sessionsRaw.find((candidate) => {
        if (!candidate || typeof candidate !== 'object') {
          return false;
        }
        const candidateId = (candidate as { id?: unknown }).id;
        return typeof candidateId === 'string' && candidateId === lastActiveSessionId;
      });
      const sessionCandidate = preferredSession ?? sessionsRaw[0];
      if (!sessionCandidate || typeof sessionCandidate !== 'object') {
        return null;
      }
      const getTokenCandidate = (sessionCandidate as { getToken?: unknown }).getToken;
      if (typeof getTokenCandidate !== 'function') {
        return null;
      }
      try {
        const token = await (getTokenCandidate as () => Promise<string | null | undefined>)();
        return typeof token === 'string' && token.length > 0 ? token : null;
      } catch {
        return null;
      }
    };
    let token: string | null = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      token = await getToken();
      token ??= await resolveTokenFromClientSessions();
      if (token) break;
      await wait(100 * (attempt + 1));
    }
    if (!token) {
      return null;
    }

    let lastError: unknown;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const nextSession = await authApi.bootstrapSession(token, { allowProvisioning });
        if (allowProvisioning) {
          setClerkProvisioningFlag(false);
        }
        return nextSession;
      } catch (caughtError) {
        if (isMissingUserIdentityError(caughtError)) {
          if (allowProvisioning) {
            return null;
          }
          throw caughtError;
        }
        if (isUserNotFoundError(caughtError)) {
          if (allowProvisioning) {
            return null;
          }
          throw caughtError;
        }
        lastError = caughtError;
        if (attempt < 1) {
          await wait(150 * (attempt + 1));
        }
      }
    }
    throw lastError;
  }, [client, getToken]);

  const clearLogoutTimer = useCallback((): void => {
    if (logoutTimer.current) {
      window.clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
  }, []);

  const applySession = useCallback(
    (next: AuthSession | null): void => {
      clearLogoutTimer();
      setCacheNamespace(next?.userId ?? null);
      if (!next) {
        clearPostAuthRedirectAttempt();
      } else {
        setManualSignOutFlag(false);
        setSignOutTransitionFlag(false);
      }
      sessionRef.current = next;
      if (next) {
        setStoredSession(next);
      } else {
        setStoredSession(null);
      }
      setSession(next);
    },
    [clearLogoutTimer],
  );

  const clearClientState = useCallback(
    (options?: { preserveSessionStorageKey?: boolean }): void => {
      const preserveSessionStorageKey = options?.preserveSessionStorageKey ?? false;
      const preserveLocalStorageKeys = new Set(['flowqa-theme-mode']);
      if (preserveSessionStorageKey) {
        preserveLocalStorageKeys.add(STORAGE_KEY);
      }
      const shouldRemoveLocalStorageKey = (key: string): boolean => {
        if (preserveLocalStorageKeys.has(key)) {
          return false;
        }
        return key.startsWith('flowqa-') || key.startsWith('datagrid:');
      };

      if (typeof window !== 'undefined') {
        try {
          const keys: string[] = [];
          for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (key && shouldRemoveLocalStorageKey(key)) {
              keys.push(key);
            }
          }
          keys.forEach((key) => {
            window.localStorage.removeItem(key);
          });
        } catch {
          // ignore storage failures
        }
        try {
          window.sessionStorage.clear();
        } catch {
          // ignore storage failures
        }
      }
      clearSessionExpiredFlag();
      if (!preserveSessionStorageKey) {
        setStoredSession(null);
      }
      void queryClient.cancelQueries();
      queryClient.clear();
    },
    [queryClient],
  );

  const performSignOut = useCallback(
    (clearSelections: boolean): void => {
      setClerkProvisioningFlag(false);
      if (clearSelections) {
        clearClientState();
      }
      applySession(null);
      setError(null);
      setLoading(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('flowqa-signout', { detail: { clearSelections } }));
      }
    },
    [applySession, clearClientState],
  );

  const signOut = useCallback(
    (options?: { redirect?: boolean; skipClerkSignOut?: boolean }): void => {
      const shouldRedirect = options?.redirect === true;
      const skipClerkSignOut = options?.skipClerkSignOut === true;
      setManualSignOutFlag(true);
      setSignOutTransitionFlag(true);
      clearSessionExpiredFlag();
      performSignOut(true);
      if (skipClerkSignOut) {
        void authApi.signOut();
        return;
      }
      if (shouldRedirect) {
        void clerkSignOut({ redirectUrl: '/sign-in' });
        return;
      }
      void clerkSignOut();
    },
    [clerkSignOut, performSignOut],
  );

  const expireSession = useCallback((): void => {
    setSessionExpiredFlag(true);
    performSignOut(true);
  }, [performSignOut]);

  useEffect(() => {
    const currentUserId = session?.userId ?? null;
    const previousUserId = previousUserIdRef.current;
    if (
      previousUserId &&
      currentUserId &&
      previousUserId !== currentUserId &&
      !getE2eAccessToken()
    ) {
      clearClientState({ preserveSessionStorageKey: true });
      window.dispatchEvent(
        new CustomEvent('flowqa-signout', { detail: { clearSelections: true } }),
      );
    }
    previousUserIdRef.current = currentUserId;
  }, [clearClientState, session?.userId]);

  useEffect(() => {
    const parsed = getStoredSession();
    const e2eToken = getE2eAccessToken();
    if (e2eToken) {
      const hasLegacyCookieToken =
        typeof parsed?.tokens.accessToken === 'string' &&
        isCookieAuthToken(parsed.tokens.accessToken);
      if (parsed && !hasLegacyCookieToken) {
        applySession(normalizeStoredSession(parsed));
      } else if (hasLegacyCookieToken) {
        setStoredSession(null);
        applySession(null);
      }
      setLoading(false);
      return;
    }
    const hasLegacyCookieToken =
      typeof parsed?.tokens.accessToken === 'string' &&
      isCookieAuthToken(parsed.tokens.accessToken);
    if (hasLegacyCookieToken) {
      setStoredSession(null);
    }
    const bootstrap = async (): Promise<void> => {
      let keepLoading = false;
      if (!isLoaded) {
        return;
      }
      if (getManualSignOutFlag()) {
        applySession(null);
        setLoading(false);
        return;
      }
      try {
        if (!hasSignedInSignal) {
          if (
            shouldPreserveSessionOnSignedOutSignal({
              hasSignedInSignal,
              currentSession: sessionRef.current,
            })
          ) {
            setLoading(false);
            return;
          }
          setManualSignOutFlag(false);
          setClerkProvisioningFlag(false);
          clearSessionExpiredFlag();
          applySession(null);
          setLoading(false);
          return;
        }
        const startedAt = Date.now();
        let refreshed: AuthSession | null = null;
        while (Date.now() - startedAt < bootstrapTimeoutMs) {
          refreshed = await loadClerkSession();
          if (refreshed) {
            break;
          }
          await wait(bootstrapRetryIntervalMs);
        }
        if (!refreshed) {
          setError({ code: 'AUTH_ERROR', message: 'Unable to load authentication session' });
          setClerkProvisioningFlag(false);
          clearSessionExpiredFlag();
          applySession(null);
          keepLoading = false;
          return;
        }
        setError(null);
        clearSessionExpiredFlag();
        applySession(mergeSessionFallbacks(refreshed, sessionRef.current));
      } catch (_err) {
        if (isIdentityMismatchError(_err)) {
          setError({
            code: 'AUTH_ERROR',
            message: 'Account identity changed. Please sign in again.',
          });
          setClerkProvisioningFlag(false);
          setManualSignOutFlag(true);
          applySession(null);
          void clerkSignOut();
          keepLoading = false;
          return;
        }
        if (isMissingUserIdentityError(_err)) {
          setError({ code: 'AUTH_ERROR', message: "Couldn't find your account." });
          setClerkProvisioningFlag(false);
          setManualSignOutFlag(false);
          applySession(null);
          keepLoading = false;
          return;
        }
        if (isUserNotFoundError(_err)) {
          setError({ code: 'AUTH_ERROR', message: "Couldn't find your account." });
          setClerkProvisioningFlag(false);
          setManualSignOutFlag(false);
          applySession(null);
          keepLoading = false;
          return;
        }
        setError({ code: 'AUTH_ERROR', message: 'Unable to load authentication session' });
        setClerkProvisioningFlag(false);
        clearSessionExpiredFlag();
        applySession(null);
        keepLoading = false;
      } finally {
        setLoading(!hasSignedInSignal ? false : keepLoading);
      }
    };
    void bootstrap();
  }, [
    applySession,
    clearClientState,
    expireSession,
    hasSignedInSignal,
    isLoaded,
    loadClerkSession,
  ]);

  useEffect(() => {
    return () => {
      clearLogoutTimer();
    };
  }, [clearLogoutTimer]);

  const signIn = useCallback(async (input: SignInPayload): Promise<void> => {
    void input;
    await Promise.resolve();
    window.location.assign('/sign-in');
  }, []);

  const signUp = useCallback(async (input: SignUpPayload): Promise<void> => {
    void input;
    await Promise.resolve();
    window.location.assign('/sign-up');
  }, []);

  const runRefreshSession = useCallback(
    async (opts?: { force?: boolean }): Promise<RefreshOutcome> => {
      const forceRefresh = opts?.force === true;
      const currentSession = sessionRef.current;
      const now = Date.now();
      if (getE2eAccessToken()) {
        return { session: currentSession, didRefresh: false };
      }
      if (!forceRefresh && now < nextRefreshAllowedAtMs.current) {
        return { session: currentSession, didRefresh: false };
      }
      if (refreshInFlight.current) {
        return await refreshInFlight.current;
      }
      const promise = (async (): Promise<RefreshOutcome> => {
        try {
          if (!hasSignedInSignal) {
            if (shouldPreserveSessionOnSignedOutSignal({ hasSignedInSignal, currentSession })) {
              nextRefreshAllowedAtMs.current = Date.now() + refreshCooldownMs;
              return { session: currentSession, didRefresh: false };
            }
            applySession(null);
            return { session: null, didRefresh: false };
          }
          const refreshed = await loadClerkSession();
          if (!refreshed) {
            if (
              shouldPreserveSessionOnRefreshMiss({ refreshedSession: refreshed, currentSession })
            ) {
              nextRefreshAllowedAtMs.current = Date.now() + refreshRateLimitBackoffMs;
              return { session: currentSession, didRefresh: false };
            }
            applySession(null);
            return { session: null, didRefresh: false };
          }
          nextRefreshAllowedAtMs.current = Date.now() + refreshCooldownMs;
          setError(null);
          const merged = mergeSessionFallbacks(refreshed, sessionRef.current);
          applySession(merged);
          return { session: merged, didRefresh: true };
        } catch (err) {
          if (err instanceof ApiError && (err.status === 429 || err.code === 'RATE_LIMITED')) {
            nextRefreshAllowedAtMs.current = Date.now() + refreshRateLimitBackoffMs;
            return { session: currentSession, didRefresh: false };
          }
          setError({ code: 'AUTH_ERROR', message: 'Unable to refresh authentication session' });
          expireSession();
          return { session: null, didRefresh: false };
        } finally {
          refreshInFlight.current = null;
        }
      })();
      refreshInFlight.current = promise;
      return await promise;
    },
    [applySession, expireSession, hasSignedInSignal, loadClerkSession],
  );

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const outcome = await runRefreshSession();
    return Boolean(outcome.session);
  }, [runRefreshSession]);
  const runRefreshSessionRef = useRef(runRefreshSession);
  const expireSessionRef = useRef(expireSession);

  useEffect(() => {
    runRefreshSessionRef.current = runRefreshSession;
  }, [runRefreshSession]);

  useEffect(() => {
    expireSessionRef.current = expireSession;
  }, [expireSession]);

  useEffect(() => {
    clearLogoutTimer();
    if (!session?.expiresAt) return;
    const delay = Math.max(session.expiresAt - Date.now(), 0);
    logoutTimer.current = window.setTimeout(() => {
      expireSession();
    }, delay);
    return () => {
      clearLogoutTimer();
    };
  }, [clearLogoutTimer, expireSession, session?.expiresAt]);

  useEffect(() => {
    if (session?.orgId && !session.orgName) {
      const orgId = session.orgId;
      if (!orgId) return;
      void (async () => {
        try {
          const organisation = await authApi.fetchOrganisation(orgId);
          applySession({ ...session, orgName: organisation.name });
        } catch {
          // ignore fetch failures; fallback will remain
        }
      })();
    }
  }, [applySession, session]);

  useEffect(() => {
    if (!session) return;
    const handleFocus = (): void => {
      void refreshSession();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshSession, session]);

  const guardState = useCallback((): AuthGuardState => {
    return { session, loading, error };
  }, [session, loading, error]);

  useEffect(() => {
    const handler = (event: StorageEvent): void => {
      if (event.key !== STORAGE_KEY) return;
      if (event.newValue === event.oldValue) return;
      if (!event.newValue) {
        applySession(null);
        return;
      }
      try {
        const parsed = JSON.parse(event.newValue) as StoredSession;
        applySession(normalizeStoredSession(parsed));
      } catch {
        applySession(null);
      }
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [applySession]);

  useEffect(() => {
    setAuthTokenProvider(() => {
      const e2eToken = getE2eAccessToken();
      if (e2eToken) return e2eToken;
      return sessionRef.current?.tokens.accessToken ?? null;
    });
    setAuthErrorHandler(async (status) => {
      const outcome = await runRefreshSessionRef.current({ force: true });
      if (outcome.didRefresh) {
        return true;
      }
      if (status === 401 && sessionRef.current) {
        expireSessionRef.current();
      }
      return false;
    });
    setAuthRefreshHandler(async () => {
      const outcome = await runRefreshSessionRef.current({ force: true });
      return outcome.didRefresh;
    });
    return () => {
      setAuthRefreshHandler(undefined);
      setAuthErrorHandler(undefined);
      setAuthTokenProvider(undefined);
    };
  }, []);

  useEffect(() => {
    const handler = (): void => {
      if (getE2eAccessToken()) return;
      expireSession();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
    };
  }, [expireSession]);

  return {
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    refreshSession,
    guardState,
    setSessionDirect: applySession,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const value = useAuthState();
  return createElement(AuthContext.Provider, { value }, children);
};

export const useAuth = (): UseAuthResult => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
